// src/ui/BoardView.tsx

import React, { useRef, useState, useCallback } from 'react';
import type { BoardData, Placement, BoardSide, LayerVisibility } from '../types';

interface BoardViewProps {
  board: BoardData | null;
  placements: Placement[];
  currentView: BoardSide;
  zoom: number;
  pan: { x: number; y: number };
  selectedId: string | null;
  currentPlacementId: string | null;
  showLayers: LayerVisibility;
  onSelect: (id: string | null) => void;
  onZoom: (zoom: number) => void;
  onPan: (pan: { x: number; y: number }) => void;
}

export const BoardView: React.FC<BoardViewProps> = ({
  board,
  placements,
  currentView,
  zoom,
  pan,
  selectedId,
  currentPlacementId,
  showLayers,
  onSelect,
  onZoom,
  onPan
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Filter placements by current view
  const visiblePlacements = placements.filter(p => p.side === currentView);
  
  // Handle mouse wheel for zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    onZoom(zoom * delta);
  }, [zoom, onZoom]);
  
  // Wheel event options for preventDefault
  const wheelOptions = { passive: false } as const;
  
  // Handle mouse down for pan
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0) { // Left click
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  }, [pan]);
  
  // Handle mouse move for pan
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      onPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart, onPan]);
  
  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  // Handle placement click
  const handlePlacementClick = useCallback((placement: Placement, e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(placement.id === selectedId ? null : placement.id);
  }, [selectedId, onSelect]);
  
  // Calculate board dimensions
  const boardWidth = board?.width || 100;
  const boardHeight = board?.height || 100;
  
  // Transform for mirroring bottom view
  const getTransform = () => {
    let transform = `translate(${pan.x}, ${pan.y}) scale(${zoom})`;
    
    if (currentView === 'bottom') {
      // Mirror X for bottom view
      transform += ` translate(${boardWidth}, 0) scale(-1, 1)`;
    }
    
    return transform;
  };
  
  return (
    <div 
      className="board-view-container"
      ref={containerRef}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      <svg
        ref={svgRef}
        className="board-svg"
        viewBox={`0 0 ${boardWidth} ${boardHeight}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <g transform={getTransform()}>
          {/* Board outline */}
          {board?.outline && board.outline.length > 0 && (
            <polygon
              points={board.outline.map(p => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke="#ff0000"
              strokeWidth="0.1"
            />
          )}
          
          {/* Board layers */}
          {board?.layers.map((layer, index) => (
            <g
              key={`${layer.id}-${index}`}
              className={`layer-${layer.role}`}
              style={{
                opacity: layer.opacity,
                color: layer.color
              }}
              dangerouslySetInnerHTML={{ __html: layer.svgContent }}
            />
          ))}
          
          {/* Drill holes */}
          {board?.drills.map((drill, i) => (
            <circle
              key={`drill-${i}`}
              cx={drill.x}
              cy={drill.y}
              r={drill.diameter / 2}
              fill="#00ffff"
              opacity={0.5}
            />
          ))}
          
          {/* Placement markers */}
          {visiblePlacements.map(placement => {
            const isSelected = placement.id === selectedId;
            const isCurrent = placement.id === currentPlacementId;
            
            // Determine color based on status
            let fill = '#f59e0b'; // pending - amber
            if (placement.status === 'placed') fill = '#22c55e'; // green
            if (placement.status === 'skipped') fill = '#6b7280'; // gray
            if (isCurrent) fill = '#3b82f6'; // blue
            
            return (
              <g
                key={placement.id}
                className={`placement-marker ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''}`}
                onClick={(e) => handlePlacementClick(placement, e)}
                style={{ cursor: 'pointer' }}
              >
                {/* Crosshair */}
                <line
                  x1={placement.x - 2}
                  y1={placement.y}
                  x2={placement.x + 2}
                  y2={placement.y}
                  stroke={fill}
                  strokeWidth="0.15"
                />
                <line
                  x1={placement.x}
                  y1={placement.y - 2}
                  x2={placement.x}
                  y2={placement.y + 2}
                  stroke={fill}
                  strokeWidth="0.15"
                />
                
                {/* Rotation indicator */}
                {placement.rotation !== 0 && (
                  <line
                    x1={placement.x}
                    y1={placement.y}
                    x2={placement.x + Math.cos(placement.rotation * Math.PI / 180) * 3}
                    y2={placement.y + Math.sin(placement.rotation * Math.PI / 180) * 3}
                    stroke={fill}
                    strokeWidth="0.1"
                    opacity={0.7}
                  />
                )}
                
                {/* Label (only for selected or current) */}
                {(isSelected || isCurrent) && (
                  <text
                    x={placement.x + 3}
                    y={placement.y - 3}
                    fontSize="2"
                    fill={fill}
                    style={{ pointerEvents: 'none' }}
                  >
                    {placement.refDes}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>
      
      {/* Overlay Controls */}
      <div className="board-controls">
        <div className="zoom-controls">
          <button onClick={() => onZoom(zoom / 1.2)} disabled={zoom <= 0.1}>-</button>
          <span>{Math.round(zoom * 100)}%</span>
          <button onClick={() => onZoom(zoom * 1.2)} disabled={zoom >= 10}>+</button>
        </div>
      </div>
    </div>
  );
};
